import { Avatar, Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import { EmployeesEmployeeQuickViewCardFragment } from 'generated/resources/graphql'
import { maxBy } from 'lodash'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { countryName } from 'modules/core/lib/countryList'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

interface Props {
  employee: EmployeesEmployeeQuickViewCardFragment
  onSeeMoreClick?: () => void
  usePreferredName?: boolean
}

const EmployeeCard: React.FC<Props> = ({ employee, onSeeMoreClick, usePreferredName }) => {
  const {
    email,
    access: { avatar },
    referenceContract,
    country,
    fullName,
  } = employee

  const { navigation } = useNavigation()
  const location = country ? countryName(country) : undefined

  const leave = maxBy(employee.upcomingLeavesConnection?.nodes || [], 'finishOn')
  const leaveType = leave?.leaveType?.translatedName
  const leaveTitle = leaveType ? leaveType : i18n.t('time_off.absent')
  const displayedName = usePreferredName ? getEmployeeDisplayName(employee) : fullName

  return (
    <Box.Motion
      boxShadow="s100"
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      borderRadius={{ all: 'abs016' }}
      paddingX="s16"
      paddingY="s16"
      background="overlayBackground"
      initial={{ pointerEvents: 'none', opacity: 0, y: 10 }}
      animate={{ pointerEvents: 'auto', opacity: 1, y: 0 }}
      exit={{ opacity: 0, pointerEvents: 'none' }}
      transition={{
        duration: 0.3,
      }}
      minWidth="s360"
      width="fit-content"
      gap="s16"
      onClick={(e) => e.stopPropagation()}
    >
      <Box flexDirection="row" gap="s16">
        <Box>
          <Avatar label={displayedName} url={buildUrl(avatar)} size="s64" />
        </Box>
        <Box gap="s4">
          <Box>
            <Text.Normal weight="medium" size="300" color="textPrimary">
              {displayedName}
            </Text.Normal>
            {(referenceContract || location) && (
              <Text.Normal weight="normal" size="200" color="textSecondary" whiteSpace="nowrap">
                {[referenceContract?.jobTitle, location].filter(Boolean).join(' · ')}
              </Text.Normal>
            )}
          </Box>
          {leave && (
            <Box width="fit-content">
              <Tag type="tangerine">{leaveTitle}</Tag>
            </Box>
          )}
          {email && (
            <Box>
              <a href={`mailto:${email}`}>
                <Text.Normal color="textLink" weight="normal" size="200">
                  {email}
                </Text.Normal>
              </a>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        paddingTop="s16"
        border={{ top: { color: 'grey400', style: 'solid', width: 's1' } }}
        flexDirection="row"
        justifyContent="flexEnd"
      >
        <Button.Router
          iconName="external link"
          to={navigation.employees.id.fullPath({ id: employee.id })}
          onClick={onSeeMoreClick}
        >
          {i18n.t('employee_quick_view.see_profile')}
        </Button.Router>
      </Box>
    </Box.Motion>
  )
}

export { EmployeeCard as Card }
